import { useEffect, useRef } from "react";
import { FullScreenMinimizeRegular } from "@fluentui/react-icons";
import styles from "../../MarkdownContent.module.scss";

interface ITableExpandedRendererProps {
  expandedTableProps: React.TableHTMLAttributes<HTMLTableElement>;
  collapseTableLabel: string;
  isDarkTheme: boolean;
  onClose: () => void;
}

export function TableExpandedRenderer({
  expandedTableProps,
  collapseTableLabel,
  isDarkTheme,
  onClose,
}: ITableExpandedRendererProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const expandedTableClassName = expandedTableProps.className
    ? `${expandedTableProps.className} table table-complex`
    : "table table-complex";

  useEffect(() => {
    closeButtonRef.current?.focus();

    const dialog = dialogRef.current;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialog) {
        return;
      }

      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]',
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return (): void => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className={styles.expandedTableOverlay}>
      <div
        className={styles.expandedTableDialog}
        role="dialog"
        aria-modal="true"
        aria-label="Expanded table"
        data-color-mode={isDarkTheme ? "dark" : "light"}
        ref={dialogRef}
      >
        <div className={styles.tableToolbar}>
          <button
            type="button"
            className={styles.tableToggleButton}
            onClick={onClose}
            aria-label={collapseTableLabel}
            ref={closeButtonRef}
          >
            <FullScreenMinimizeRegular />&nbsp;{collapseTableLabel}
          </button>
        </div>
        <div
          className={styles.expandedTableWrapper}
          role="region"
          aria-label="Expanded table content"
        >
          <table
            {...expandedTableProps}
            className={expandedTableClassName}
          />
        </div>
      </div>
    </div>
  );
}

